import Swal from "sweetalert2";

/**
 * Show an error alert (red icon, for API/operation failures).
 * @param {string} message
 */
export function swalError(message) {
  return Swal.fire({
    title: "Error",
    text: message,
    icon: "error",
    buttonsStyling: false,
    customClass: { confirmButton: "btn btn-danger" },
  });
}

/**
 * Show an error alert with HTML content (red icon, for rich error messages).
 * @param {string} title
 * @param {string} htmlContent
 */
export function swalErrorHtml(title, htmlContent) {
  return Swal.fire({
    title,
    html: htmlContent,
    icon: "error",
    buttonsStyling: false,
    width: 600,
    customClass: { confirmButton: "btn btn-danger" },
  });
}

/**
 * Show an error alert tailored for API errors. Extracts ProblemDetails
 * (RFC 7807) validation errors when present, otherwise falls back to
 * a plain message.
 * @param {unknown} err - axios error or any thrown value
 * @param {string} [fallback="An error occurred"] - used when no message is found
 */
export function swalApiError(err, fallback = "An error occurred") {
  const data = err && err.response ? err.response.data : null;

  // ProblemDetails validation errors: { title, errors: { field: [msg, ...] } }
  if (data && data.errors && typeof data.errors === "object") {
    const items = [];
    for (const [field, msgs] of Object.entries(data.errors)) {
      const list = Array.isArray(msgs) ? msgs : [String(msgs)];
      for (const m of list) {
        const cleanField = String(field).replace(/^\$\./, "");
        items.push(
          `<li><strong>${escapeHtml(cleanField)}:</strong> ${escapeHtml(m)}</li>`,
        );
      }
    }
    const title = data.title || fallback;
    const html = items.length
      ? `<ul class="text-start ps-3 mb-0">${items.join("")}</ul>`
      : escapeHtml(data.detail || fallback);
    return swalErrorHtml(title, html);
  }

  // Plain string body
  if (typeof data === "string" && data.trim()) {
    return swalError(data);
  }

  // Common shapes: { message }, { detail }, { title }
  const msg =
    (data && (data.message || data.detail || data.title)) ||
    (err && err.message) ||
    fallback;
  return swalError(msg);
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Show a success notification (green icon, auto-closes after 2s).
 * @param {string} message
 */
export function swalSuccess(message) {
  return Swal.fire({
    title: "Success!",
    text: message,
    icon: "success",
    timer: 2000,
    timerProgressBar: true,
    showConfirmButton: false,
  });
}

/**
 * Show a warning alert (yellow icon, for validation warnings).
 * @param {string} message
 */
export function swalWarning(message) {
  // Blur active element so focus does not return to the submit button
  // after the dialog closes (prevents accidental re-submit on Enter key)
  if (document.activeElement) document.activeElement.blur();
  return Swal.fire({
    title: "Warning",
    text: message,
    icon: "warning",
    buttonsStyling: false,
    customClass: { confirmButton: "btn btn-warning" },
  });
}

/**
 * Show an informational alert (blue icon).
 * @param {string} message
 */
export function swalInfo(message) {
  return Swal.fire({
    title: "Info",
    text: message,
    icon: "info",
    buttonsStyling: false,
    customClass: { confirmButton: "btn btn-primary" },
  });
}

/**
 * Show a confirmation dialog. Returns true if user confirmed, false otherwise.
 * @param {string} title
 * @param {string} text
 * @param {object} [options]
 * @param {string} [options.icon="question"]
 * @param {string} [options.confirmText="Yes"]
 * @param {string} [options.cancelText="Cancel"]
 * @param {string} [options.confirmClass="btn-primary"]
 * @param {boolean} [options.isDanger=false] — shortcut: sets icon to "warning" and button to "btn-danger"
 * @returns {Promise<boolean>}
 */
export async function swalConfirm(title, text, options = {}) {
  const isDanger = options.isDanger || false;
  const { isConfirmed } = await Swal.fire({
    title,
    text,
    icon: isDanger ? "warning" : (options.icon || "question"),
    showCancelButton: true,
    confirmButtonText: options.confirmText || "Yes",
    cancelButtonText: options.cancelText || "Cancel",
    buttonsStyling: false,
    customClass: {
      confirmButton: `btn ${isDanger ? "btn-danger" : (options.confirmClass || "btn-primary")} me-2`,
      cancelButton: "btn btn-light",
    },
  });
  return isConfirmed;
}

/**
 * Themed input prompt (replacement for browser-native window.prompt).
 * Returns the entered string trimmed, or null if the user cancelled or
 * left the field empty when `required` is true.
 *
 * @param {string} title    Modal title.
 * @param {string} text     Optional descriptive text shown above the input.
 * @param {object} options  { placeholder, required (default true), inputType ('text'|'textarea') }
 */
export async function swalPrompt(title, text, options = {}) {
  const required = options.required !== false;
  const result = await Swal.fire({
    title,
    text: text || undefined,
    input: options.inputType || "text",
    inputPlaceholder: options.placeholder || "",
    showCancelButton: true,
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    buttonsStyling: false,
    customClass: {
      confirmButton: "btn btn-primary me-2",
      cancelButton: "btn btn-light",
      input: "form-control",
    },
    inputValidator: required
      ? (value) => (!value || !value.trim() ? "A response is required" : null)
      : undefined,
  });
  if (!result.isConfirmed) return null;
  const v = (result.value || "").trim();
  return v.length === 0 ? null : v;
}
